import type { Control, FieldErrors, UseFormRegisterReturn } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { PasswordVisibilityIcon } from '@/shared/lib/password-visibility'
import { loginInputNativeProps } from '@/features/auth/styles/loginSplitStyles'
import { errorInputSx } from '@/features/auth/styles/authFormStyles'
import {
  WEB_SURVEY_ACCOUNT_NAVY,
  webSurveyAccountAsteriskSx,
  webSurveyAccountFieldBlockSx,
  webSurveyAccountInputSx,
  webSurveyAccountLabelSx,
  webSurveyAccountPasswordInputSx,
} from '../styles/webSurveyAccountStyles'
import type { WebSurveyAccountFormData } from '../lib/web-survey-account-schema'
import { WEB_SURVEY_REGISTRATION_PARTIES } from '../lib/web-survey-registration-type'

export function WebSurveyRequiredMark() {
  return (
    <Box component="span" aria-hidden sx={webSurveyAccountAsteriskSx}>
      {' *'}
    </Box>
  )
}

interface WebSurveyTextFieldProps {
  readonly id: string
  readonly label: string
  readonly required?: boolean
  readonly type?: string
  readonly autoComplete?: string
  readonly placeholder?: string
  readonly errorMessage?: string
  readonly testId: string
  readonly disabled?: boolean
  readonly readOnly?: boolean
  readonly value?: string
  readonly fieldSx?: object
  readonly inputSx?: object
  readonly register?: UseFormRegisterReturn
}

export function WebSurveyTextField({
  id,
  label,
  required = false,
  type = 'text',
  autoComplete,
  placeholder,
  errorMessage,
  testId,
  disabled = false,
  readOnly = false,
  value,
  fieldSx,
  inputSx,
  register,
}: WebSurveyTextFieldProps) {
  const hasError = Boolean(errorMessage)
  const { ref: inputRef, ...field } = register ?? { ref: undefined }
  return (
    <Box sx={{ ...webSurveyAccountFieldBlockSx, ...fieldSx }}>
      <Typography component="label" htmlFor={id} sx={webSurveyAccountLabelSx}>
        {label}
        {required ? <WebSurveyRequiredMark /> : null}
      </Typography>
      <OutlinedInput
        id={id}
        type={type}
        fullWidth
        autoComplete={autoComplete}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        error={hasError}
        sx={{ ...webSurveyAccountInputSx, ...inputSx, ...(hasError ? errorInputSx : {}) }}
        inputProps={{
          ...loginInputNativeProps,
          'data-testid': testId,
          'aria-invalid': hasError,
          'aria-describedby': hasError ? `${id}-error` : undefined,
        }}
        inputRef={inputRef}
        {...(register ? field : { value })}
      />
      {errorMessage ? (
        <Typography
          id={`${id}-error`}
          role="alert"
          data-testid={`${testId}-error`}
          sx={{ color: '#ef4444', fontSize: '14px', lineHeight: '20px' }}
        >
          {errorMessage}
        </Typography>
      ) : null}
    </Box>
  )
}

interface WebSurveyPasswordFieldProps {
  readonly id: string
  readonly label: string
  readonly placeholder: string
  readonly autoComplete: string
  readonly errorMessage?: string
  readonly testId: string
  readonly visible: boolean
  readonly onToggleVisibility: () => void
  readonly register: UseFormRegisterReturn
}

export function WebSurveyPasswordField({
  id,
  label,
  placeholder,
  autoComplete,
  errorMessage,
  testId,
  visible,
  onToggleVisibility,
  register,
}: WebSurveyPasswordFieldProps) {
  const hasError = Boolean(errorMessage)
  const hideLabel = visible ? 'Hide password' : 'Show password'
  const { ref: inputRef, ...field } = register
  return (
    <Box sx={webSurveyAccountFieldBlockSx}>
      <Typography component="label" htmlFor={id} sx={webSurveyAccountLabelSx}>
        {label}
        <WebSurveyRequiredMark />
      </Typography>
      <OutlinedInput
        id={id}
        type={visible ? 'text' : 'password'}
        fullWidth
        autoComplete={autoComplete}
        placeholder={placeholder}
        error={hasError}
        sx={{
          ...webSurveyAccountPasswordInputSx,
          ...(hasError ? errorInputSx : {}),
        }}
        inputProps={{
          ...loginInputNativeProps,
          'data-testid': testId,
          'aria-invalid': hasError,
          'aria-describedby': hasError ? `${id}-error` : undefined,
        }}
        inputRef={inputRef}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label={hideLabel}
              onClick={onToggleVisibility}
              edge="end"
              data-testid={`${testId}-visibility`}
              sx={{ color: WEB_SURVEY_ACCOUNT_NAVY }}
            >
              <PasswordVisibilityIcon visible={visible} size={24} />
            </IconButton>
          </InputAdornment>
        }
        {...field}
      />
      {errorMessage ? (
        <Typography
          id={`${id}-error`}
          role="alert"
          data-testid={`${testId}-error`}
          sx={{ color: '#ef4444', fontSize: '14px', lineHeight: '20px' }}
        >
          {errorMessage}
        </Typography>
      ) : null}
    </Box>
  )
}

interface WebSurveyRegistrationSelectProps {
  readonly control: Control<WebSurveyAccountFormData>
  readonly errors: FieldErrors<WebSurveyAccountFormData>
}

export function WebSurveyRegistrationSelect({
  control,
  errors,
}: WebSurveyRegistrationSelectProps) {
  const errorMessage = errors.registeredPoliticalParty?.message
  const hasError = Boolean(errorMessage)
  return (
    <Box sx={webSurveyAccountFieldBlockSx}>
      <Typography
        component="label"
        htmlFor="web-survey-registration-type"
        sx={webSurveyAccountLabelSx}
      >
        Are you currently registered as
        <WebSurveyRequiredMark />
      </Typography>
      <Controller
        name="registeredPoliticalParty"
        control={control}
        render={({ field }) => (
          <Box data-testid="web-survey-registration-type">
            <Select
              id="web-survey-registration-type"
              displayEmpty
              fullWidth
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={hasError}
              input={<OutlinedInput />}
              sx={{
                ...webSurveyAccountInputSx,
                ...(hasError ? errorInputSx : {}),
                '& .MuiSelect-select': {
                  py: '14px',
                  px: '16px',
                  color: field.value ? WEB_SURVEY_ACCOUNT_NAVY : '#9ca3af',
                },
              }}
              inputProps={{
                'aria-invalid': hasError,
                'aria-describedby': hasError ? 'web-survey-registration-type-error' : undefined,
              }}
            >
              <MenuItem value="" disabled>
                Select an option
              </MenuItem>
              {WEB_SURVEY_REGISTRATION_PARTIES.map((party) => (
                <MenuItem key={party} value={party}>
                  {party}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      />
      {errorMessage ? (
        <Typography
          id="web-survey-registration-type-error"
          role="alert"
          data-testid="web-survey-registration-type-error"
          sx={{ color: '#ef4444', fontSize: '14px', lineHeight: '20px' }}
        >
          {errorMessage}
        </Typography>
      ) : null}
    </Box>
  )
}
